import React, { useState, useEffect, useMemo } from 'react';

// --- Helper & UI Components ---

const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-20">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-md p-6 mb-6 ${className}`}>
    {children}
  </div>
);

// --- NEW: Analysis Report Components ---

const ReportContent = ({ htmlContent }) => {
  // This component safely renders the HTML from Airtable and applies dashboard-specific styling.
  // Note: You may need to add these styles to your global CSS file for them to apply correctly.
  return (
    <div className="report-prose pt-4">
      <style jsx global>{`
        .report-prose h2 {
          font-size: 1.25rem; /* 20px */
          font-weight: 700;
          color: #334155;
          border-bottom: 2px solid #0ea5e9;
          padding-bottom: 8px;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }
        .report-prose h2:first-child {
          margin-top: 0;
        }
        .report-prose p, .report-prose li {
          font-size: 0.9375rem; /* 15px */
          color: #475569;
          line-height: 1.6;
        }
        .report-prose ul {
          padding-left: 20px;
          list-style-type: disc;
        }
        .report-prose li {
          margin-bottom: 0.5rem;
        }
        .report-prose strong {
          color: #334155;
        }
      `}</style>
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </div>
  );
};


const AnalysisReport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        // Fetches data from the 'analysis' table
        const res = await fetch(`/api/airtable?table=analysis`);
        if (!res.ok) {
          throw new Error('Failed to fetch analysis report.');
        }
        const data = await res.json();
        // Assuming the latest report is the first record returned
        if (data.records && data.records.length > 0) {
          setReport(data.records[0].fields);
        }
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const ChevronIcon = ({ isOpen }) => (
    <svg className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
    </svg>
  );

  return (
    <div className="bg-white rounded-xl shadow-md mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-6 text-left font-bold text-lg text-white bg-[#00142E] rounded-t-xl focus:outline-none"
      >
        <span>Latest Video Activity Analysis</span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="px-6 pb-6 border-t border-gray-200">
          {loading && <div className="p-4 text-center">Loading Report...</div>}
          {error && <div className="p-4 text-center text-red-500">Error: {error}</div>}
          {report ? (
            <div>
              <p className="text-sm text-gray-500 mb-2">Report Date: {new Date(report.Report_Date).toLocaleDateString()}</p>
              <ReportContent htmlContent={report.Content} />
            </div>
          ) : (
            !loading && <div className="p-4 text-center text-gray-500">No report found.</div>
          )}
        </div>
      )}
    </div>
  );
};


// --- Main Application Components (Existing Code) ---

const Header = ({ logoUrl }) => (
  <header className="bg-white shadow-sm p-4 sticky top-0 z-20">
    <div className="container mx-auto flex justify-between items-center">
      {/* --- MODIFICATION: Changed heading case --- */}
      <h1 className="text-2xl font-bold text-gray-800">Ford Europe - YouTube competitor analysis</h1>
      <img src={logoUrl} alt="VML Logo" className="h-8 w-auto" />
    </div>
  </header>
);

const FilterControls = ({ brands, markets, onBrandChange, onMarketChange }) => (
  <div className="bg-white p-4 rounded-xl shadow-md mb-6 sticky top-[80px] z-10">
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex-grow">
        <label htmlFor="brand-select" className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
        <select
          id="brand-select"
          onChange={(e) => onBrandChange(e.target.value)}
          className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="">All Brands</option>
          {brands.map(brand => <option key={brand} value={brand}>{brand}</option>)}
        </select>
      </div>
      <div className="flex-grow">
        <label htmlFor="market-select" className="block text-sm font-medium text-gray-700 mb-1">Market</label>
        <select
          id="market-select"
          onChange={(e) => onMarketChange(e.target.value)}
          className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="">All Markets</option>
          {markets.map(market => <option key={market} value={market}>{market}</option>)}
        </select>
      </div>
    </div>
  </div>
);

const YouTubePlayer = ({ videoUrl }) => {
  // This function is now more robust and uses a regular expression to find the Video ID.
  const getEmbedUrl = (url) => {
    if (!url || typeof url !== 'string') return null;

    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);

    const videoId = (match && match[2].length === 11) ? match[2] : null;
    
    if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
    }
    
    console.error("Could not extract YouTube video ID from URL:", url);
    return null;
  };

  const embedUrl = getEmbedUrl(videoUrl);

  if (!embedUrl) {
    return <div className="aspect-w-16 aspect-h-9 bg-gray-200 flex items-center justify-center rounded-lg"><p className="text-red-500 text-sm p-4 text-center">Invalid or unsupported YouTube URL.</p></div>;
  }

  return (
    <div className="aspect-w-16 aspect-h-9">
      <iframe
        src={embedUrl}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full rounded-lg shadow-lg"
      ></iframe>
    </div>
  );
};

const VideoList = ({ videos }) => {
  if (videos.length === 0) {
    return (
      <Card>
        <p className="text-center text-gray-500">No videos match the current filters.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {videos.map(video => (
        <Card key={video.id}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
            <div className="md:col-span-2">
              <YouTubePlayer videoUrl={video.Video_URL} />
            </div>
            <div className="md:col-span-3">
              <h3 className="text-xl font-bold text-gray-900">{video.Video_Title || 'No Title'}</h3>
              <p className="text-sm text-gray-500 mb-3">by {video.Channel_Name || 'Unknown Channel'}</p>
              <p className="text-base text-gray-700 mb-4">{video.Description || 'No description available.'}</p>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">{video.Brand}</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">{video.Market}</span>
                <span className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full">
                  Published: {video.Published ? new Date(video.Published).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const Pagination = ({ videosPerPage, totalVideos, paginate, currentPage }) => {
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(totalVideos / videosPerPage); i++) {
        pageNumbers.push(i);
    }

    if (pageNumbers.length <= 1) return null;

    return (
        <nav className="mt-8">
            <ul className="flex justify-center space-x-2">
                {pageNumbers.map(number => (
                    <li key={number}>
                        <button
                            onClick={() => paginate(number)}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${currentPage === number ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            {number}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
};


// --- Main App Component ---

function App() {
  // State
  const [allAds, setAllAds] = useState([]);
  const [brands, setBrands] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const VML_LOGO_URL = "https://i.ibb.co/1wxvDjJ/BLACK-Logo-Snowflake-VML.png";
  const VIDEOS_PER_PAGE = 15;

  // Data Fetching
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/airtable?table=youtube_ads`);
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.error || 'Failed to fetch data from Airtable.');
        }
        const data = await res.json();
        const records = (data.records || []).map(rec => ({ id: rec.id, ...rec.fields }));

        // Sort by most recent first
        records.sort((a, b) => new Date(b.Published) - new Date(a.Published));

        setAllAds(records);

        // Populate dropdowns with unique, sorted values
        const uniqueBrands = [...new Set(records.map(r => r.Brand).filter(Boolean))].sort();
        const uniqueMarkets = [...new Set(records.map(r => r.Market).filter(Boolean))].sort();
        setBrands(uniqueBrands);
        setMarkets(uniqueMarkets);

      } catch (err)
 {
        setError(err.message);
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtering Logic
  const filteredAds = useMemo(() => {
    return allAds.filter(ad => {
      const brandMatch = !selectedBrand || ad.Brand === selectedBrand;
      const marketMatch = !selectedMarket || ad.Market === selectedMarket;
      return brandMatch && marketMatch;
    });
  }, [allAds, selectedBrand, selectedMarket]);

  // Pagination Logic
  const indexOfLastVideo = currentPage * VIDEOS_PER_PAGE;
  const indexOfFirstVideo = indexOfLastVideo - VIDEOS_PER_PAGE;
  const currentVideos = filteredAds.slice(indexOfFirstVideo, indexOfLastVideo);

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    setCurrentPage(1);
  };

  const handleMarketChange = (market) => {
    setSelectedMarket(market);
    setCurrentPage(1);
  };

  return (
    <div className="bg-gray-100 min-h-screen font-sans">
      <Header logoUrl={VML_LOGO_URL} />
      <main className="container mx-auto p-6">
        {loading && <LoadingSpinner />}
        {error && (
          <div className="text-center text-red-600 bg-red-100 p-6 rounded-md shadow-md">
            <h2 className="text-xl font-bold mb-2">Error Loading Dashboard</h2>
            <pre className="text-sm text-left whitespace-pre-wrap">{error}</pre>
          </div>
        )}
        {!loading && !error && (
          <>
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Overview</h2>
              <p className="text-gray-700">
                This tool collects videos from the identified YouTube channels and is updated daily. You may use the drop down filters to select the brands and markets you are interested in. There is also a detailed AI Analysis section in which you are able to see an overall analysis of video activity from the tracked channels. To add additional competitors or markets please speak to your VML agency team.
              </p>
            </div>

            <FilterControls
              brands={brands}
              markets={markets}
              onBrandChange={handleBrandChange}
              onMarketChange={handleMarketChange}
            />
            
            <AnalysisReport />

            <VideoList videos={currentVideos} />
            <Pagination 
                videosPerPage={VIDEOS_PER_PAGE}
                totalVideos={filteredAds.length}
                paginate={setCurrentPage}
                currentPage={currentPage}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default App;
